import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Layers, Palette, FileCheck, Truck, Repeat } from "lucide-react";
import { Reveal, SplitLines, EASE } from "../components/Reveal";
import { WhatsAppIcon } from "../components/WhatsAppIcon";
import { ZoomImage } from "../components/Lightbox";
import { Sparkles } from "../components/Sparkles";
import { BUSINESS, waLink } from "../data/business";
import { usePageMeta } from "../lib/usePageMeta";

const STEPS = [
  ["01", "Send the spec", <>Shape, sieve size in mm, clarity, colour and total carats needed — on <WhatsAppIcon size={14} className="text-[#25D366]" /> or the enquiry form.</>],
  ["02", "Parcel preview", "We sort and colour-match the parcel, then send photos and a video under daylight lamp within hours."],
  ["03", "Approval & invoice", "Confirm the parcel; we issue a GST invoice with full CVD/HPHT disclosure and pieces-per-carat."],
  ["04", "Insured dispatch", "Pan-India dispatch in 24–48 hours, or same-day pickup at Abids, Hyderabad."],
];

const BENEFITS = [
  [Layers, "Calibrated by sieve", "-2 to +15 trade sizes, tight mm tolerance for faster setting."],
  [Palette, "Colour-matched parcels", "D–F and G–H runs matched under a single light source."],
  [Package, "Minimum 10 ct per sieve", "Small enough for a first order, scalable to monthly volumes."],
  [Repeat, "Standing orders", "Reserve recurring parcels for production; credit terms for regular partners."],
  [FileCheck, "Full disclosure", "Growth method, treatment status and origin on every invoice."],
  [Truck, "Fast turnaround", "Stock parcels dispatched within 48 hours across India."],
];

const SIEVE = [["-2", "0.50–1.20 mm"], ["+2 to -4", "1.20–1.40 mm"], ["+4 to -6.5", "1.40–1.70 mm"], ["+6.5 to -11", "1.70–2.80 mm"], ["+11 to -15", "2.80–3.40 mm"], ["+15", "3.40 mm +"]];

export default function BulkOrder() {
  usePageMeta(
    "Wholesale Lab Grown Diamonds & Melee Parcels for Jewellers | B2B Diamond Supplier Hyderabad – Padmavati",
    "B2B wholesale lab grown diamonds: calibrated CVD and HPHT melee by sieve size, colour-matched diamond parcels, IGI certified pointers, GST invoice and pan-India dispatch from Abids, Hyderabad."
  );
  return (
    <main data-testid="bulk-order-page" className="pt-[72px]">
      <section className="relative py-24 lg:py-32 grain">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-6">Bulk order · For jewellers & manufacturers</p>
            <h1 className="font-display font-light text-ivory text-[clamp(2.8rem,7vw,6.5rem)] leading-[0.95]">
              <SplitLines lines={["Wholesale,", <span key="a" className="text-saffron italic">by the sieve.</span>]} />
            </h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-8 text-ivory/65 font-light leading-relaxed max-w-xl text-base sm:text-lg">
              Calibrated lab-grown melee and pointer parcels for production houses, retailers and designers. Tell us the sieve, clarity
              and carats you need — we sort, match and dispatch with a GST invoice.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, ease: EASE, duration: 0.9 }} className="mt-10 flex flex-wrap gap-4">
              <Link to="/enquiry?buyer=B2B" data-testid="bulk-enquiry-btn" className="btn-gold"><WhatsAppIcon size={16} /> Start bulk enquiry</Link>
              <a href={waLink(`Hello ${BUSINESS.name}, I'd like to place a bulk order for lab-grown melee. Sieve / clarity / carats:`)} target="_blank" rel="noreferrer" data-testid="bulk-whatsapp-btn" className="btn-outline"><WhatsAppIcon size={17} className="text-[#25D366]" /> {BUSINESS.phone}</a>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1.3, ease: EASE }} className="lg:col-span-5 relative">
            <div className="absolute -inset-3 border border-gold/25" />
            <div className="overflow-hidden aspect-[4/3] relative img-frame" style={{ clipPath: "polygon(0 0, 100% 0, 100% 90%, 90% 100%, 0 100%)" }}>
              <ZoomImage src="/img/parcel.jpg" alt="Wholesale calibrated CVD lab grown melee diamond parcel with tweezers – bulk diamonds for jewellers" className="h-full w-full object-cover" />
              <Sparkles count={10} />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-gold/10 bg-[#0d0d0d] py-24">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
          <Reveal><p className="eyebrow mb-6">How it works</p></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-gold/15 border border-gold/15" data-testid="bulk-steps">
            {STEPS.map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.08} className="bg-[#0d0d0d] p-8 group">
                <p className="font-display text-5xl text-gold/30 group-hover:text-saffron transition-colors duration-500">{n}</p>
                <h3 className="font-display text-2xl text-ivory mt-4">{t}</h3>
                <p className="text-sm text-ivory/60 font-light leading-relaxed mt-3">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow mb-6">Why source from us</p></Reveal>
            <div className="grid sm:grid-cols-2 gap-5" data-testid="bulk-benefits">
              {BENEFITS.map(([Icon, t, d], i) => (
                <Reveal key={t} delay={i * 0.06} className="border border-gold/15 hover:border-saffron/50 transition-colors p-6 bg-[#101010]">
                  <Icon size={18} strokeWidth={1.5} className="text-gold" />
                  <h3 className="font-display text-xl text-ivory mt-4">{t}</h3>
                  <p className="text-sm text-ivory/60 font-light mt-2 leading-relaxed">{d}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <p className="eyebrow mb-6">Sieve reference</p>
            <table className="w-full text-sm border border-gold/15" data-testid="bulk-sieve-table">
              <thead><tr className="text-[10px] uppercase tracking-[0.25em] text-gold bg-gold/5"><th className="text-left px-4 py-3 font-medium">Sieve / trade</th><th className="text-right px-4 py-3 font-medium">Approx. mm</th></tr></thead>
              <tbody>{SIEVE.map(([a, b]) => <tr key={a} className="border-t border-gold/10 text-ivory/80"><td className="px-4 py-3 font-mono text-xs">{a}</td><td className="px-4 py-3 text-right">{b}</td></tr>)}</tbody>
            </table>
            <p className="text-xs text-ivory/40 mt-4 leading-relaxed">Quantities are quoted in total carats. Ask for pieces-per-carat to plan your setting work.</p>
            <Link to="/browse" data-testid="bulk-browse-link" className="btn-outline mt-8 !text-[11px] !py-2.5">Browse live parcels</Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
